/**
 * Payment status formatting utilities
 * Provides consistent payment status display across the application
 * Standardizes backend status values to human-readable, title-case format
 */

/**
 * Maps backend payment status values to standardized display format
 * @param {string|null|undefined} status - Payment status from backend
 * @returns {string} - Formatted status string in title case (e.g., "Partially Paid")
 */
export const formatPaymentStatus = (status) => {
  if (!status || typeof status !== 'string') {
    return 'Pending';
  }

  // Normalize to lowercase for comparison
  const normalizedStatus = status.toLowerCase().trim();

  // Map backend statuses to display format
  const statusMap = {
    'paid': 'Paid',
    'partially_paid': 'Partially Paid',
    'partially paid': 'Partially Paid',
    'overdue': 'Overdue',
    'pending': 'Pending',
    'refunded': 'Refunded',
    'partial': 'Partially Paid',
    'completed': 'Paid',
    'incomplete': 'Pending',
    'failed': 'Failed',
    'cancelled': 'Cancelled',
    'cancelled': 'Cancelled'
  };

  // Return mapped status or capitalize first letter of each word if not in map
  if (statusMap[normalizedStatus]) {
    return statusMap[normalizedStatus];
  }

  // Fallback: Convert snake_case or camelCase to Title Case
  return status
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Gets color classes for payment status badge
 * @param {string|null|undefined} status - Payment status
 * @returns {string} - Tailwind CSS classes for status badge
 */
export const getPaymentStatusColor = (status) => {
  if (!status || typeof status !== 'string') {
    return 'bg-gray-100 text-gray-800';
  }

  const normalizedStatus = status.toLowerCase().trim();

  const colorMap = {
    'paid': 'bg-green-100 text-green-800',
    'partially_paid': 'bg-yellow-100 text-yellow-800',
    'partially paid': 'bg-yellow-100 text-yellow-800',
    'overdue': 'bg-red-100 text-red-800',
    'pending': 'bg-gray-100 text-gray-800',
    'refunded': 'bg-blue-100 text-blue-800',
    'partial': 'bg-yellow-100 text-yellow-800',
    'completed': 'bg-green-100 text-green-800',
    'failed': 'bg-red-100 text-red-800',
    'cancelled': 'bg-gray-100 text-gray-800'
  };

  return colorMap[normalizedStatus] || 'bg-gray-100 text-gray-800';
};

